import { isSubscriptionExpiredError } from "../../utils/subscriptionExpiredDialog";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Table } from "../../components/Table";
import PageTitle from "../../components/PageTitle";
import { columns, data } from "../../utils/constants";
import { motion, AnimatePresence } from "framer-motion";
import {
  deleteNotices,
  fetchData,
  postData,
} from "../../api/apiService";
import { ServiceType } from "../../utils/enums";
import {
  Link,
  useLocation,
  useParams,
  useNavigationType,
} from "react-router-dom";
import useTenantNavigate from "../../hooks/useTenantNavigate";
import TenantLink from "../../components/TenantLink";
import UserFilterDropdown, {
  ALL_USERS,
  toQueryParam as userFilterToQueryParam,
} from "../../components/UserFilterDropdown";
import WarningImage from "../../assets/images/Warning.png";
import {
  Button,
  Tag,
  DatePicker,
  Select,
  Space,
  Row,
  Col,
  Card,
  Popover,
  Modal,
  Breadcrumb,
  Tooltip,
  Input,
  Progress,
  Alert,
  Typography,
  Spin,
} from "antd";
import { FaFilter, FaList, FaPlus } from "react-icons/fa";
import { FaArrowsRotate } from "react-icons/fa6";
import { toast } from "react-toastify";
import {
  CalendarOutlined,
  CloseOutlined,
  DeleteFilled,
  SyncOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getDocType } from "../../utils/helpers";
import { useAxiosPost } from "../../hooks/useAxios";
import {
  ArrowRight,
  ArrowRightIcon,
  FilterIcon,
  HomeIcon,
  ChevronDown,
  ChevronUp,
  Download,
  CheckCircle,
} from "lucide-react";
import DashboardButton from "../../components/DashboardButton";
import ClientAutoComplete from "../../components/ClientAutoComplete";
import TableV2 from "../../components/TableV2";
import DownloadDataButton from "../../components/DownloadDataButton";
import NoticeSummaryViewer from "../../components/NoticeSummaryViewer";
import { useFeatureEnabled } from "../../hooks/usePlanFeatures";
import { FEATURE_KEYS } from "../../utils/featureKeys";
import FetchNoticesConfirmModal from "../../components/FetchNoticesConfirmModal";
import DeleteAllNoticesButton from "../../components/DeleteAllNoticesButton";
import BulkStatusButton from "../../components/BulkStatusButton";
import { CLIENTS, DOCUMENTS, LAMBDA } from "../../api/endpointMap";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Group e-proceeding rows into the SAME proceedings the income-tax portal shows.
// The proceeding identity is the portal's `proceedingReqId`, which is the first
// segment of `unique_e_pro_id` (the lambda builds it as
// `${proceedingReqId}-${headerSeqNo}` for notices and
// `${proceedingReqId}-closure-${seq}` for closure orders). Grouping on this —
// NOT on proceeding_name+assessment_year — is what makes our counts match the
// portal: distinct proceedings that share a name and have a blank Assessment
// Year (e.g. several "Recovery Process" / "Issue Letter") must stay separate,
// which the old name+AY key wrongly merged.
// Groups sorted by latest issued date desc; notices within each group by issued
// date desc. Each group splits off closure ORDER(s) so the card can offer the
// portal's two buttons ("View Closure Order" + "View Notices/Orders (N)").
// Module-level pure function so the FYA and FYI partitions can each be grouped
// with the SAME logic (no duplicated grouping code).
const proceedingKey = (n) => {
  const uid = n.unique_e_pro_id && String(n.unique_e_pro_id).trim();
  // First segment = proceedingReqId. `-closure-...` still splits to the same id.
  if (uid) return `proc:${uid.split("-")[0]}`;
  // Legacy / manually-added rows without a unique_e_pro_id: fall back to
  // name+AY so they don't all collapse into one bucket.
  const ay =
    (n.assessment_year && String(n.assessment_year).trim()) || "Not Available";
  const name = (n.proceeding_name && String(n.proceeding_name).trim()) || "-";
  return `nameay:${name}|||${ay}`;
};
const buildEpGroups = (notices) => {
  const _t = (d) => (d ? new Date(d).getTime() || 0 : 0);
  const map = new Map();
  for (const n of notices || []) {
    const key = proceedingKey(n);
    if (!map.has(key)) {
      const ay =
        (n.assessment_year && String(n.assessment_year).trim()) ||
        "Not Available";
      const name =
        (n.proceeding_name && String(n.proceeding_name).trim()) || "-";
      map.set(key, {
        groupKey: key,
        proceeding_name: name,
        assessment_year: ay,
        // All notices in a proceeding share these — carried for the
        // Self / Of Other PAN/TAN split and third-party card labels.
        proceeding_type: n.proceeding_type || "Self",
        name_of_assessee: n.name_of_assessee || "",
        pan: n.pan || "",
        notices: [],
      });
    }
    map.get(key).notices.push(n);
  }
  const isClosureOrder = (n) =>
    String(n?.notice_section || "").trim().toLowerCase() === "closure order";
  const groups = Array.from(map.values()).map((g) => {
    g.notices.sort((a, b) => _t(b.notice_sent_date) - _t(a.notice_sent_date));
    const latest = g.notices.reduce(
      (m, x) => Math.max(m, _t(x.notice_sent_date)),
      0
    );
    const orders = g.notices.filter(isClosureOrder);
    const plainNotices = g.notices.filter((n) => !isClosureOrder(n));
    return {
      ...g,
      allNotices: g.notices,
      notices: plainNotices,
      orders,
      count: plainNotices.length,
      orderCount: orders.length,
      latestIssueDate: latest,
    };
  });
  groups.sort((a, b) => b.latestIssueDate - a.latestIssueDate);
  return groups;
};

export const Eproceedings = ({ serviceType }) => {
  // Plan-feature gate for the "View Summary" notice-list column. Driven by
  // /api/v1/subscriptions/features (VIEW_NOTICE_SUMMARY). When disabled the
  // whole column is dropped (see the TableV2 columns filter below).
  const summaryFeatureEnabled = useFeatureEnabled(
    FEATURE_KEYS.VIEW_NOTICE_SUMMARY,
  );
  const [originalData, setOriginalData] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [ePdataList, setEPDataList] = useState([]);
  const [responseOutstandingList, setResponseOutstandingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assessmentYearOptions, setAssessmentYearOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([
    {
      label: "Open",
      value: "Open",
    },
    {
      label: "Close",
      value: "Close",
    },
  ]);
  const [showFilters, setShowFilters] = useState(true);
  const [activeFilters, setActiveFilters] = useState({
    client_name: true,
    assessmentYear: true,
    noticeSentDate: true,
    dueDate: true,
    dueWithin: true,
    proceeding_status: true,
    notice_din: true,
    notice_name: true,
    notice_section: true,
    notice_type: true,
    notice_communication_reference_id: true,
    notice_sent_date: true,
  });

  const [mounted, setMounted] = useState(false);
  // User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). Sentinel ALL_USERS
  // → omit assigned_user_id from the payload (BE applies role-default).
  const [userFilter, setUserFilter] = useState(ALL_USERS);

  const { clientId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchClientType = queryParams.get("noticeType") ?? "active";
  const clientName = queryParams.get("client_name");

  const noticeType = queryParams.get("type");
  const dueDateParam = queryParams.get("due_date");
  const activeTabParam = queryParams.get("activeTab");

  // Storage key for filter persistence
  const FILTER_STORAGE_KEY = `eproceedings_filters_${clientId || "all"}`;
  // Storage key for active tab persistence
  const TAB_STORAGE_KEY = `eproceedings_activeTab_${clientId || "all"}`;

  // Detect navigation type: POP = browser back/forward, PUSH/REPLACE = fresh navigation
  const navigationType = useNavigationType();

  // Initialize activeNoticeTab - restore from session on back navigation, otherwise from URL or default
  const [activeNoticeTab, setActiveNoticeTab] = useState(() => {
    // If activeTab is in URL params, use it (coming from detail page)
    if (activeTabParam) {
      return activeTabParam === "response" ? "response" : "eproceeding";
    }
    // On browser back navigation, try to restore from sessionStorage
    if (navigationType === "POP") {
      const savedTab = sessionStorage.getItem(TAB_STORAGE_KEY);
      if (savedTab === "response" || savedTab === "eproceeding") {
        return savedTab;
      }
    }
    // Default to eproceeding
    return "eproceeding";
  });

  // Save active tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(TAB_STORAGE_KEY, activeNoticeTab);
  }, [activeNoticeTab, TAB_STORAGE_KEY]);

  // Load saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert date strings back to moment objects if needed
        if (parsed.dueDate && Array.isArray(parsed.dueDate)) {
          parsed.dueDate = parsed.dueDate.map((d) => (d ? moment(d) : null));
        }
        if (parsed.noticeSentDate && Array.isArray(parsed.noticeSentDate)) {
          parsed.noticeSentDate = parsed.noticeSentDate.map((d) =>
            d ? moment(d) : null
          );
        }
        if (
          parsed.date_of_demand_raised &&
          Array.isArray(parsed.date_of_demand_raised)
        ) {
          parsed.date_of_demand_raised = parsed.date_of_demand_raised.map((d) =>
            d ? moment(d) : null
          );
        }
        if (
          parsed.date_of_service_notice &&
          Array.isArray(parsed.date_of_service_notice)
        ) {
          parsed.date_of_service_notice = parsed.date_of_service_notice.map(
            (d) => (d ? moment(d) : null)
          );
        }
        return parsed;
      }
    } catch (e) {
      console.error("Error loading saved filters:", e);
    }
    return null;
  };

  const getDefaultFilters = () => {
    return noticeType == "failed_login"
      ? { client_name: null }
      : {
          noticeSentDate: null,
          dueDate: null,
          assessmentYear: null,
          proceeding_status: null,
          response_status: null,
          client_name: null,
          notice_din: null,
          notice_communication_reference_id: null,
          notice_name: null,
          notice_section: null,
          notice_type: null,
          demand_reference_no: null,
          section_code: null,
          date_of_demand_raised: null,
          date_of_service_notice: null,
          notice_status: null,
          response_type: null,
          notice_sent_date: null,
        };
  };

  // Clear filters from sessionStorage (used when navigating via breadcrumb)
  const clearSavedFilters = () => {
    try {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } catch (e) {
      console.error("Error clearing saved filters:", e);
    }
  };

  // Only restore filters on browser back (POP), not on fresh navigation (PUSH/REPLACE)
  const [filterValues, setFilterValues] = useState(() => {
    if (navigationType === "POP") {
      // Browser back/forward - restore saved filters
      const savedFilters = getSavedFilters();
      return savedFilters || getDefaultFilters();
    } else {
      // Fresh navigation (clicking card/link) - clear saved filters and start fresh
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
      return getDefaultFilters();
    }
  });

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      // Convert moment objects to strings for storage
      const filtersToSave = { ...filterValues };
      if (filtersToSave.dueDate && Array.isArray(filtersToSave.dueDate)) {
        filtersToSave.dueDate = filtersToSave.dueDate.map((d) =>
          d ? d.format() : null
        );
      }
      if (
        filtersToSave.noticeSentDate &&
        Array.isArray(filtersToSave.noticeSentDate)
      ) {
        filtersToSave.noticeSentDate = filtersToSave.noticeSentDate.map((d) =>
          d ? d.format() : null
        );
      }
      if (
        filtersToSave.date_of_demand_raised &&
        Array.isArray(filtersToSave.date_of_demand_raised)
      ) {
        filtersToSave.date_of_demand_raised =
          filtersToSave.date_of_demand_raised.map((d) =>
            d ? d.format() : null
          );
      }
      if (
        filtersToSave.date_of_service_notice &&
        Array.isArray(filtersToSave.date_of_service_notice)
      ) {
        filtersToSave.date_of_service_notice =
          filtersToSave.date_of_service_notice.map((d) =>
            d ? d.format() : null
          );
      }
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (e) {
      console.error("Error saving filters:", e);
    }
  }, [filterValues, FILTER_STORAGE_KEY]);

  // Get filtered client name for breadcrumb display
  const getFilteredClientName = () => {
    return filterValues?.client_name?.trim() || null;
  };

  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [processedClients, setProcessedClients] = useState([]);

  // Progress polling state for failed login retry
  const [parallelFetchLogName, setParallelFetchLogName] = useState(null);
  const [fetchProgress, setFetchProgress] = useState(null);
  const [batchPollingInterval, setBatchPollingInterval] = useState(null);
  const [syncCompletionModal, setSyncCompletionModal] = useState(false);
  const isBatchFetchActive = useRef(false);

  const { Text, Title } = Typography;

  const hasActiveFilters = (filters) =>
    Object.values(filters).some((v) => v !== null && v !== "");

  const [selectedNoticeIds, setSelectedNoticeIds] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientsData, setClientsData] = useState([]);
  const [noticeSectionsData, setNoticeSectionsData] = useState([]);

  // --- Portal-style grouping for the E-Proceeding tab (LOCAL-ONLY, do NOT push).
  // Layer 1 = group cards (proceeding_name + assessment_year); Layer 2 = that
  // group's notices in the existing TableV2; Layer 3 = the notice detail page.
  const [epGroupView, setEpGroupView] = useState("groups"); // 'groups' | 'notices'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [allEpNotices, setAllEpNotices] = useState([]); // full e-proceeding set for grouping
  const [groupsLoading, setGroupsLoading] = useState(false);
  // For-Your-Action / For-Your-Information sub-tab within E-Proceeding, mirroring
  // the income-tax portal. Partitions the grouped cards by the row-level `fyi`
  // flag (false = FYA, true = FYI).
  const [epFyiTab, setEpFyiTab] = useState("fya"); // 'fya' | 'fyi'
  // Self / "Of Other PAN/TAN" top-level toggle (proceeding_type), mirroring the
  // portal's "View e-Proceeding related to: Self | Of Other PAN/TAN".
  const [epPanTab, setEpPanTab] = useState("self"); // 'self' | 'other'

  const [limitStart, setLimitStart] = useState(0);
  const [limit, setLimit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [eproceedingscount, setEproceedingsCount] = useState(0);
  const [responseToOutstandingCount, setReponseToOutstandingCount] =
    useState(0);
  const [failedLoginList, setFailedLoginList] = useState([]);
  const [failedLoginCount, setFailedLoginCount] = useState(0);

  //test

  const navigate = useTenantNavigate();

  const formatDate = (date) => {
    if (!date) return "";

    if (typeof date.format === "function") {
      return date.format("YYYY-MM-DD");
    }

    if (
      Object.prototype.toString.call(date) === "[object Date]" &&
      !isNaN(date.getTime())
    ) {
      return moment(date).format("YYYY-MM-DD");
    }

    const m = moment(date);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  };

  const handleOk = () => {
    setIsDeleteModalOpen(false);
    handleDeleteNotices();
  };

  const handleCancel = () => {
    setIsDeleteModalOpen(false);
  };

  // Legacy Frappe-era data fetches. The doctype-keyed REST endpoints
  // (`/Income Tax Client`, `/api/v1/income-tax/notice-sections`) never
  // existed on the FastAPI backend, so these calls have been returning
  // 404s since the migration. The state both functions populated
  // (`clientsData`, `noticeSectionsData`) is unread elsewhere — they're
  // dead. Left as no-ops so the call sites at the init effect below
  // don't need to be touched, and so console isn't spammed with 404s
  // every time someone clicks a calendar date.
  const getClientsData = async () => {};
  const getNoticeSectionsList = async () => {};

  const buildEProceedingFilters = (doctype = "E Proceeding") => {
    const filters = [["disabled", "=", 0]];

    if (clientId) {
      filters.push([doctype, "client", "=", clientId, false]);
    }

    if (dueDateParam) {
      const formattedDate = formatDate(dueDateParam);
      filters.push([doctype, "response_due_date", "=", formattedDate, false]);
    }

    if (noticeType == "last_3_days_notices") {
      const today = new Date();
      const previousDate = new Date();
      previousDate.setDate(today.getDate() - 2);
      filters.push([
        doctype,
        "notice_sent_date",
        "between",
        [formatDate(previousDate), formatDate(today)],
        false,
      ]);
    } else if (noticeType == "over_due_notices") {
      const today = new Date();
      filters.push([
        doctype,
        "response_due_date",
        "<",
        formatDate(today),
        false,
      ]);
      filters.push([doctype, "response_due_date", "is", "set", false]);
    } else if (noticeType == "last_15_days_notices") {
      const today = new Date();
      const pastDate = new Date();
      pastDate.setDate(today.getDate() - 15);
      filters.push([
        doctype,
        "notice_sent_date",
        ">=",
        formatDate(pastDate),
        false,
      ]);
    } else if (noticeType == "open_notices") {
      filters.push([doctype, "proceeding_status", "=", "Open", false]);
    }

    if (["over_due_notices", "due_7_days_notices", "open_notices"].includes(noticeType)) {
      filters.push([doctype, "response_status", "!=", "Submitted", false]);
    }

    if (filterValues.client_name && filterValues.client_name.trim()) {
      filters.push([
        doctype,
        "client_name",
        "like",
        `%${filterValues.client_name}%`,
        false,
      ]);
    }

    if (filterValues.assessmentYear && filterValues.assessmentYear.trim()) {
      filters.push([
        doctype,
        "assessment_year",
        "like",
        `%${filterValues.assessmentYear}%`,
        false,
      ]);
    }

    if (filterValues.dueDate) {
      const [startDate, endDate] = filterValues.dueDate;
      filters.push([
        "response_due_date",
        "between",
        [formatDate(startDate), formatDate(endDate)],
      ]);
    }

    if (filterValues.noticeSentDate) {
      const [startDate, endDate] = filterValues.noticeSentDate;
      filters.push([
        "notice_sent_date",
        "between",
        [formatDate(startDate), formatDate(endDate)],
      ]);
    }

    if (filterValues.proceeding_status) {
      filters.push([
        "proceeding_status",
        "in",
        [filterValues.proceeding_status],
      ]);
    }

    if (filterValues.response_status) {
      filters.push(["response_status", "=", filterValues.response_status]);
    }

    if (filterValues.notice_communication_reference_id) {
      filters.push([
        "notice_communication_reference_id",
        "like",
        `%${filterValues.notice_communication_reference_id}%`,
      ]);
    }

    return filters;
  };

  const buildFailedLoginFilters = () => {
    const filters = [
      ["Login Failed", "portal", "=", "Income Tax", false],
      ["disabled", "=", 0],
    ];

    if (filterValues.client_name && filterValues.client_name.trim()) {
      filters.push([
        "Login Failed",
        "client_name",
        "like",
        `%${filterValues.client_name}%`,
        false,
      ]);
    }

    return filters;
  };

  const buildResponseFilters = (doctype = "Response to Outstanding Demand") => {
    const filters = [["disabled", "=", 0]];

    if (clientId) {
      filters.push([doctype, "client", "=", clientId, false]);
    }

    if (dueDateParam) {
      const formattedDate = formatDate(dueDateParam);
      filters.push([
        doctype,
        "date_of_service_notice",
        "=",
        formattedDate,
        false,
      ]);
    }

    if (noticeType == "last_3_days_notices") {
      const today = new Date();
      const previousDate = new Date();
      previousDate.setDate(today.getDate() - 2);
      filters.push([
        doctype,
        "date_of_demand_raised",
        "between",
        [formatDate(previousDate), formatDate(today)],
        false,
      ]);
    } else if (noticeType == "over_due_notices") {
      const today = new Date();
      filters.push([
        doctype,
        "date_of_service_notice",
        "<",
        formatDate(today),
        false,
      ]);
      filters.push([doctype, "date_of_service_notice", "is", "set", false]);
    } else if (noticeType == "last_15_days_notices") {
      const today = new Date();
      const pastDate = new Date();
      pastDate.setDate(today.getDate() - 15);
      filters.push([
        doctype,
        "date_of_demand_raised",
        ">=",
        formatDate(pastDate),
        false,
      ]);
    } else if (noticeType == "open_notices") {
      filters.push([doctype, "notice_status", "in", ["Open", "Pending Payment"], false]);
    }

    if (["over_due_notices", "due_7_days_notices", "open_notices"].includes(noticeType)) {
      filters.push([doctype, "response_status", "!=", "Submitted", false]);
    }

    if (filterValues.client_name && filterValues.client_name.trim()) {
      filters.push([
        doctype,
        "client_name",
        "like",
        `%${filterValues.client_name}%`,
        false,
      ]);
    }

    if (filterValues.assessmentYear && filterValues.assessmentYear.trim()) {
      filters.push([
        doctype,
        "assessment_year",
        "like",
        `%${filterValues.assessmentYear}%`,
        false,
      ]);
    }

    if (filterValues.date_of_demand_raised) {
      const [startDate, endDate] = filterValues.date_of_demand_raised;
      filters.push([
        "date_of_demand_raised",
        "between",
        [formatDate(startDate), formatDate(endDate)],
      ]);
    }

    if (filterValues.date_of_service_notice) {
      const [startDate, endDate] = filterValues.date_of_service_notice;
      filters.push([
        "date_of_service_notice",
        "between",
        [formatDate(startDate), formatDate(endDate)],
      ]);
    }

    if (filterValues.response_type) {
      filters.push([
        "response_type",
        "like",
        `%${filterValues.response_type}%`,
      ]);
    }

    if (filterValues.demand_reference_no) {
      filters.push([
        "demand_reference_no",
        "like",
        `%${filterValues.demand_reference_no}%`,
      ]);
    }

    if (filterValues.section_code) {
      filters.push(["section_code", "like", `%${filterValues.section_code}%`]);
    }

    if (filterValues.notice_status) {
      filters.push([
        "notice_status",
        "like",
        `%${filterValues.notice_status}%`,
      ]);
    }

    if (filterValues.response_status) {
      filters.push(["response_status", "=", filterValues.response_status]);
    }

    return filters;
  };

  // Map frontend noticeType URL param → backend notice_mode preset
  const getNoticeMode = (type) => {
    const map = {
      total_notices: "total",
      last_3_days_notices: "last_3_days",
      last_15_days_notices: "last_15_days",
      open_notices: "open",
      over_due_notices: "over_due",
      due_7_days_notices: "due_within_7",
    };
    return map[type] || null;
  };

  // Fetch ALL records (all pages) matching current filters — used by
  // TableV2 PDF/CSV download buttons. Backend caps page_length at 500
  // so loop in batches until last page returns < 500 records. Builds
  // same params + endpoint as the list-fetch functions below.
  const fetchAllForExport = async () => {
    const PAGE_SIZE = 500;
    const all = [];
    let start = 0;

    const buildParamsForTab = (start, page_length) => {
      const params = { start, page_length };
      if (noticeType === "failed_login") {
        if (filterValues.client_name?.trim()) {
          params.client_name = filterValues.client_name.trim();
        }
        return { endpoint: "/api/v1/income-tax/failed-logins", params, recordsPath: "result.data" };
      }
      if (clientId) params.client = clientId;
      if (clientName) params.client_name = clientName;
      const mode = getNoticeMode(noticeType);
      if (mode) params.notice_mode = mode;
      if (filterValues.client_name?.trim()) params.client_name = filterValues.client_name.trim();
      if (filterValues.assessmentYear?.trim()) params.assessment_year = filterValues.assessmentYear.trim();
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) params.assigned_user_id = userParam;

      if (activeNoticeTab === "eproceeding") {
        if (dueDateParam) params.due_date = formatDate(dueDateParam);
        if (filterValues.notice_din?.trim?.()) params.notice_din = filterValues.notice_din.trim();
        if (filterValues.notice_communication_reference_id?.trim?.()) {
          params.notice_communication_reference_id = filterValues.notice_communication_reference_id.trim();
        }
        if (filterValues.notice_name?.trim?.()) params.notice_name = filterValues.notice_name.trim();
        if (filterValues.notice_section?.trim?.()) params.notice_section = filterValues.notice_section.trim();
        if (filterValues.proceeding_status) {
          params.proceeding_status = Array.isArray(filterValues.proceeding_status)
            ? filterValues.proceeding_status[0]
            : filterValues.proceeding_status;
        }
        if (filterValues.response_status) params.response_status = filterValues.response_status;
        if (filterValues.noticeSentDate && Array.isArray(filterValues.noticeSentDate)) {
          const [from, to] = filterValues.noticeSentDate;
          if (from && to) { params.sent_from = formatDate(from); params.sent_to = formatDate(to); }
        }
        if (filterValues.dueDate && Array.isArray(filterValues.dueDate)) {
          const [from, to] = filterValues.dueDate;
          if (from && to) { params.due_from = formatDate(from); params.due_to = formatDate(to); }
        }
        return { endpoint: "/api/v1/income-tax/e-proceedings", params, recordsPath: "data.records" };
      }

      if (dueDateParam) params.service_date = formatDate(dueDateParam);
      if (filterValues.demand_reference_no?.trim?.()) params.demand_reference_no = filterValues.demand_reference_no.trim();
      if (filterValues.section_code?.trim?.()) params.section_code = filterValues.section_code.trim();
      if (filterValues.notice_status?.trim?.()) params.notice_status = filterValues.notice_status.trim();
      if (filterValues.response_status) params.response_status = filterValues.response_status;
      if (filterValues.response_type?.trim?.()) params.response_type = filterValues.response_type.trim();
      if (filterValues.date_of_demand_raised && Array.isArray(filterValues.date_of_demand_raised)) {
        const [from, to] = filterValues.date_of_demand_raised;
        if (from && to) { params.demand_raised_from = formatDate(from); params.demand_raised_to = formatDate(to); }
      }
      if (filterValues.date_of_service_notice && Array.isArray(filterValues.date_of_service_notice)) {
        const [from, to] = filterValues.date_of_service_notice;
        if (from && to) { params.service_from = formatDate(from); params.service_to = formatDate(to); }
      }
      return { endpoint: "/api/v1/income-tax/demands", params, recordsPath: "data.records" };
    };

    while (true) {
      const { endpoint, params, recordsPath } = buildParamsForTab(start, PAGE_SIZE);
      const resp = await fetchData(endpoint, params);
      // Pre-format creation as the page-list render does, so CSV/PDF
      // show "May 19th 2026, 2:25:26 pm" instead of raw ISO. TableV2's
      // export reads row[col.key] and skips render() for string values.
      const batch = recordsPath === "result.data"
        ? (resp?.result?.data || []).map((r) => ({
            ...r,
            creation: r.creation
              ? moment(r.creation).format("MMMM Do YYYY, h:mm:ss a")
              : "",
          }))
        : (resp?.data?.records || []);
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      start += PAGE_SIZE;
    }
    return all;
  };

  // --- Grouping: fetch ALL e-proceedings for the client so we can group them
  // (proceeding_name + assessment_year) portal-style. Reuses fetchAllForExport
  // which already respects the active tab + current filters. Refetched when the
  // tab, client, filters, or a refresh (via ePdataList) change.
  useEffect(() => {
    if (activeNoticeTab !== "eproceeding" || noticeType === "failed_login") return;
    let cancelled = false;
    (async () => {
      setGroupsLoading(true);
      try {
        const all = await fetchAllForExport();
        // Match the shape of ePdataList (fetchEProceedingsList adds notice_type,
        // which the delete flow reads to pick the doctype).
        const withType = (Array.isArray(all) ? all : []).map((item) => ({
          ...item,
          notice_type: "E Proceeding",
        }));
        if (!cancelled) setAllEpNotices(withType);
      } catch (e) {
        if (!cancelled) setAllEpNotices([]);
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNoticeTab, noticeType, clientId, filterValues, ePdataList]);

  // Group a set of e-proceeding rows by (proceeding_name + assessment_year),
  // portal-style. Groups sorted by latest issued date desc; notices within each
  // group by issued date desc. Pure helper so the FYA and FYI partitions below
  // can each be grouped independently with the same logic (no duplication).
  // Two-level split, mirroring the income-tax portal:
  //   Level 1 — Self vs "Of Other PAN/TAN" (proceeding_type)
  //   Level 2 — For Your Action vs For Your Information (fyi)
  // Build all four buckets once; the active bucket follows the two tab states.
  const groupsByType = useMemo(() => {
    const isOther = (n) => n.proceeding_type === "Of Other PAN/TAN";
    const build = (notices) => ({
      fya: buildEpGroups(notices.filter((n) => !n.fyi)),
      fyi: buildEpGroups(notices.filter((n) => !!n.fyi)),
    });
    return {
      self: build(allEpNotices.filter((n) => !isOther(n))),
      other: build(allEpNotices.filter((n) => isOther(n))),
    };
  }, [allEpNotices]);

  const activeTypeGroups =
    epPanTab === "other" ? groupsByType.other : groupsByType.self;
  const fyaGroups = activeTypeGroups.fya;
  const fyiGroups = activeTypeGroups.fyi;
  // The cards shown reflect BOTH the Self/Other-PAN-TAN toggle and the FYA/FYI
  // sub-tab (mirrors the portal's two-level layout).
  const epGroups = epFyiTab === "fyi" ? fyiGroups : fyaGroups;

  // Proceeding counts per Self/Other type, for the top toggle labels.
  const selfProceedingCount =
    groupsByType.self.fya.length + groupsByType.self.fyi.length;
  const otherProceedingCount =
    groupsByType.other.fya.length + groupsByType.other.fyi.length;

  // Render flags for the E-Proceeding grouped drill-down.
  const isEpTab =
    activeNoticeTab === "eproceeding" && noticeType !== "failed_login";
  const showGroupCards = isEpTab && epGroupView === "groups";
  const showGroupNotices = isEpTab && epGroupView === "notices" && !!selectedGroup;
  // `mode` = 'notices' (everything except the closure order) | 'orders'
  // (only the closure order[s]) — mirrors the portal's two buttons.
  const openGroup = (g, mode = "notices") => {
    setSelectedGroup({ ...g, viewMode: mode });
    setEpGroupView("notices");
    setSelectedNoticeIds([]);
  };
  // Rows shown in Layer 2 for the current selection.
  const groupRows = selectedGroup
    ? selectedGroup.viewMode === "orders"
      ? selectedGroup.orders || []
      : selectedGroup.notices || []
    : [];
  const backToGroups = () => {
    setEpGroupView("groups");
    setSelectedGroup(null);
    setSelectedNoticeIds([]);
  };

  const fetchEProceedingsList = async (start, itemsPerPage) => {
    setLoading(true);
    try {
      const params = {
        start: start,
        page_length: itemsPerPage,
      };
      if (clientId) params.client = clientId;
      if (clientName) params.client_name = clientName;  // from URL

      const mode = getNoticeMode(noticeType);
      if (mode) params.notice_mode = mode;

      // Exact due date from calendar click (URL param)
      if (dueDateParam) {
        params.due_date = formatDate(dueDateParam);
      }

      // Column-level filters from filterValues state
      if (filterValues.client_name?.trim()) {
        params.client_name = filterValues.client_name.trim();
      }
      if (filterValues.assessmentYear?.trim()) {
        params.assessment_year = filterValues.assessmentYear.trim();
      }
      if (filterValues.notice_din?.trim?.()) {
        params.notice_din = filterValues.notice_din.trim();
      }
      if (filterValues.notice_communication_reference_id?.trim?.()) {
        params.notice_communication_reference_id = filterValues.notice_communication_reference_id.trim();
      }
      if (filterValues.notice_name?.trim?.()) {
        params.notice_name = filterValues.notice_name.trim();
      }
      if (filterValues.notice_section?.trim?.()) {
        params.notice_section = filterValues.notice_section.trim();
      }
      if (filterValues.proceeding_status) {
        params.proceeding_status = Array.isArray(filterValues.proceeding_status)
          ? filterValues.proceeding_status[0]
          : filterValues.proceeding_status;
      }
      if (filterValues.response_status) {
        params.response_status = filterValues.response_status;
      }
      if (filterValues.noticeSentDate && Array.isArray(filterValues.noticeSentDate)) {
        const [from, to] = filterValues.noticeSentDate;
        if (from && to) {
          params.sent_from = formatDate(from);
          params.sent_to = formatDate(to);
        }
      }
      if (filterValues.dueDate && Array.isArray(filterValues.dueDate)) {
        const [from, to] = filterValues.dueDate;
        if (from && to) {
          params.due_from = formatDate(from);
          params.due_to = formatDate(to);
        }
      }

      // §7.2: scope to a specific user / Unassigned / All. ALL_USERS →
      // omit param entirely so BE auto-scopes per role.
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) params.assigned_user_id = userParam;

      const data = await fetchData("/api/v1/income-tax/e-proceedings", params);

      const fetchedData = data?.data?.records || [];
      const dataWithType = fetchedData.map((item) => ({
        ...item,
        notice_type: "E Proceeding",
      }));
      setEPDataList(dataWithType);
      setTotalRecords(data?.data?.total_records || 0);
      setEproceedingsCount(data?.data?.total_records || 0);
    } catch (err) {
      toast.error(
        "Failed to fetch E-Proceedings: " +
          (typeof err === "string" ? err : err?.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchResponseToOutstandingDemand = async (start, itemsPerPage) => {
    setLoading(true);
    try {
      const params = {
        start: start,
        page_length: itemsPerPage,
      };
      if (clientId) params.client = clientId;
      if (clientName) params.client_name = clientName;  // from URL

      const mode = getNoticeMode(noticeType);
      if (mode) params.notice_mode = mode;

      // Exact service date from calendar click (URL param)
      if (dueDateParam) {
        params.service_date = formatDate(dueDateParam);
      }

      // Column-level filters from filterValues state
      if (filterValues.client_name?.trim()) {
        params.client_name = filterValues.client_name.trim();
      }
      if (filterValues.assessmentYear?.trim()) {
        params.assessment_year = filterValues.assessmentYear.trim();
      }
      if (filterValues.demand_reference_no?.trim?.()) {
        params.demand_reference_no = filterValues.demand_reference_no.trim();
      }
      if (filterValues.section_code?.trim?.()) {
        params.section_code = filterValues.section_code.trim();
      }
      if (filterValues.notice_status?.trim?.()) {
        params.notice_status = filterValues.notice_status.trim();
      }
      if (filterValues.response_status) {
        params.response_status = filterValues.response_status;
      }
      if (filterValues.response_type?.trim?.()) {
        params.response_type = filterValues.response_type.trim();
      }
      if (filterValues.date_of_demand_raised && Array.isArray(filterValues.date_of_demand_raised)) {
        const [from, to] = filterValues.date_of_demand_raised;
        if (from && to) {
          params.demand_raised_from = formatDate(from);
          params.demand_raised_to = formatDate(to);
        }
      }
      if (filterValues.date_of_service_notice && Array.isArray(filterValues.date_of_service_notice)) {
        const [from, to] = filterValues.date_of_service_notice;
        if (from && to) {
          params.service_from = formatDate(from);
          params.service_to = formatDate(to);
        }
      }

      // §7.2: same user-filter applies to demands.
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) params.assigned_user_id = userParam;

      const data = await fetchData("/api/v1/income-tax/demands", params);

      const fetchedData = data?.data?.records || [];
      const dataWithType = fetchedData.map((item) => ({
        ...item,
        notice_type: "Response to Outstanding Demand",
      }));
      setResponseOutstandingList(dataWithType);
      setTotalRecords(data?.data?.total_records || 0);
      setReponseToOutstandingCount(data?.data?.total_records || 0);
    } catch (err) {
      toast.error(
        "Failed to fetch Response to Outstanding Demand: " +
          (typeof err === "string" ? err : err?.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchFailedLoginList = async (start, itemsPerPage) => {
    setLoading(true);
    try {
      // Legacy `POST /api/v1/documents/list` with body `{doctype: "Login
      // Failed", filters, fields, ...}` returns 404 on FastAPI — the
      // generic Frappe resource-list path was never migrated. Use the
      // dedicated `GET /api/v1/income-tax/failed-logins` endpoint; same
      // response shape (`data.result.data` + `data.result.total_records`)
      // so downstream state updates stay identical.
      const params = {
        start: start,
        page_length: itemsPerPage,
      };
      if (filterValues.client_name && filterValues.client_name.trim()) {
        params.client_name = filterValues.client_name.trim();
      }
      // R70 — forward the "Filter by user" dropdown's value so the
      // Failed Logins card honours the same user filter as the other
      // cards (Total/Open/Over Due/Due 7d/Last 3 days). Paired with
      // the matching BE-side support in income_tax.py.
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) params.assigned_user_id = userParam;

      const data = await fetchData(
        "/api/v1/income-tax/failed-logins",
        params
      );

      const fetchedData = data?.result?.data || [];
      setFailedLoginList(fetchedData);
      setFailedLoginCount(data?.result?.total_records || 0);
    } catch (err) {
      toast.error(
        "Failed to fetch Failed Login records: " + (err.message || err)
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newLimitStart, newLimit) => {
    console.log("PAGE CHANGED", newLimitStart);
    setLimitStart(newLimitStart);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setLimitStart(0);
  };

  useEffect(() => {
    if (mounted) {
      if (noticeType === "failed_login") {
        fetchFailedLoginList(limitStart, limit);
      } else if (activeNoticeTab === "eproceeding") {
        fetchEProceedingsList(limitStart, limit);
      } else {
        fetchResponseToOutstandingDemand(limitStart, limit);
      }
    }
    // userFilter is intentionally a dep — re-fetch when the user-filter
    // dropdown changes (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2).
  }, [activeNoticeTab, limitStart, limit, noticeType, userFilter]);

  // Check for active fetch on page load (handles page refresh mid-fetch)
  // Scoped to Income Tax portal so GST/TDS fetches don't affect this page
  const checkActiveFetch = async () => {
    try {
      const response = await postData(
        LAMBDA.GET_FETCH_STATUS,
        { portal_type: "Income Tax" }
      );
      // FastAPI returns service payload under response.data.result (same fix as Clients.jsx).
      const result =
        response?.data?.result || response?.result || response?.message || {};
      if (result?.status === "success" && !result?.is_complete) {
        // A fetch is still in progress — disable buttons and resume polling
        setIsProcessingAll(true);
        setParallelFetchLogName(result.log_name);
        startBatchPolling(result.log_name);
      }
    } catch (err) {
      console.error("Error checking active fetch:", err);
    }
  };

  useEffect(() => {
    if (!mounted) {
      if (noticeType === "failed_login") {
        fetchFailedLoginList(limitStart, limit);
      } else {
        fetchResponseToOutstandingDemand(limitStart, limit);
        fetchEProceedingsList(limitStart, limit);
      }
      checkActiveFetch();
      setMounted(true);
    }
  }, []);

  const toggleFilter = (filterName) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const handleFilterValueChange = (filterName, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Total Notices search/filter bar (Layer-1 group-cards view). These keys are
  // already honoured by buildParamsForTab -> the grouping useEffect refetches +
  // regroups whenever filterValues changes, so the bar needs no extra wiring.
  const TOTAL_NOTICES_FILTER_KEYS = [
    "notice_din",
    "notice_communication_reference_id",
    "assessmentYear",
    "response_status",
    "noticeSentDate",
  ];
  const hasActiveTotalNoticesFilter = TOTAL_NOTICES_FILTER_KEYS.some((k) => {
    const v = filterValues?.[k];
    return Array.isArray(v) ? v.length > 0 : !!v;
  });
  const clearTotalNoticesFilters = () => {
    setFilterValues((prev) => {
      const next = { ...prev };
      TOTAL_NOTICES_FILTER_KEYS.forEach((k) => {
        next[k] = null;
      });
      return next;
    });
  };

  const applyFilters = () => {
    if (mounted) {
      setLoading(true);
      setLimitStart(0);
      if (noticeType === "failed_login") {
        fetchFailedLoginList(0, limit);
      } else if (activeNoticeTab === "eproceeding") {
        fetchEProceedingsList(0, limit);
      } else {
        fetchResponseToOutstandingDemand(0, limit);
      }
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (noticeType !== "failed_login") {
        // setLimitStart(0);
        applyFilters();
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [
    filterValues.dueDate,
    filterValues.noticeSentDate,
    filterValues.proceeding_status,
    filterValues.response_status,
    filterValues.client_name,
    filterValues.assessmentYear,
    filterValues.notice_din,
    filterValues.notice_name,
    filterValues.notice_section,
    filterValues.notice_type,
    filterValues.notice_communication_reference_id,
    filterValues.demand_reference_no,
    filterValues.section_code,
    filterValues.date_of_demand_raised,
    filterValues.date_of_service_notice,
    filterValues.notice_status,
    filterValues.response_type,
    filterValues.notice_sent_date,
  ]);

  useEffect(() => {
    getClientsData();
    getNoticeSectionsList();
  }, []);

  const eProceedingColumns = [
    {
      label: "Client Name",
      key: "client_name",
      filterType: "autocomplete",
      filterable: true,
      sortable: true,
      filterName: "client_name",
    },
    {
      label: "Notice Communication ID",
      key: "notice_communication_reference_id",
      filterType: "text",
      filterable: true,
      sortable: true,
      filterName: "notice_communication_reference_id",
    },
    {
      label: "Assessment Year",
      key: "assessment_year",
      filterType: "text",
      filterable: true,
      sortable: true,
      filterName: "assessmentYear",
    },
    {
      label: "Issued Date",
      key: "notice_sent_date",
      filterType: "date",
      filterable: true,
      sortable: true,
      filterName: "noticeSentDate",
      render: (row) => {
        const date = row?.notice_sent_date;
        const formattedDate = date
          ? date.split("-").reverse().join("-")
          : "N/A";
        return (
          <div className="flex justify-center mr-2">
            <Tag color="#108ee9">{formattedDate}</Tag>
          </div>
        );
      },
    },
    {
      label: "Response Due Date",
      key: "response_due_date",
      filterType: "date",
      filterable: true,
      sortable: true,
      filterName: "dueDate",
      render: (row) => {
        const date = row?.response_due_date;
        const formattedDate = date
          ? date.split("-").reverse().join("-")
          : "N/A";
        return (
          <div className="flex justify-center mr-2">
            <Tag color="#108ee9">{formattedDate}</Tag>
          </div>
        );
      },
    },
    {
      label: "View Summary",
      key: "view_summary",
      filterable: false,
      filterType: "none",
      preventRowClick: true,

      render: (row) => {
        // No notice letter PDF available for this row → surface a
        // muted "No Notice File" label so the cell isn't ambiguously
        // blank. Truthiness of `notice_letter` matches the
        // detail-endpoint wrap helper convention.
        if (!row?.notice_letter) {
          return (
            <span className="text-slate-400 text-xs italic ml-14">
              No Notice File
            </span>
          );
        }
        return (
          <div className="ml-14">
            <NoticeSummaryViewer
              variant="table"
              doctype={getDocType(
                row?.notice_type == "E Proceeding"
                  ? "EPROCEEDING"
                  : "RESPONSEOUTSTANDING"
              )}
              buttonClassName="rounded-full w-4 text-lg"
              docname={row?.name}
            />
          </div>
        );
      },
    },
    {
      label: "Response Status",
      key: "response_status",
      filterType: "select",
      filterable: true,
      sortable: true,
      filterName: "response_status",
      filterOptions: [
        { label: "Pending", value: "Pending" },
        { label: "Response Generated", value: "Response Generated" },
        { label: "Response Finalized", value: "Response Finalized" },
        { label: "Submitted", value: "Submitted" },
      ],
      render: (row) => {
        const statusColors = {
          Pending: "#d9d9d9",
          "Response Generated": "#faad14",
          "Response Finalized": "#1890ff",
          Submitted: "#52c41a",
        };
        return (
          <div className="flex items-center h-14 justify-center">
            <Tag
              color={statusColors[row?.response_status] || "#d9d9d9"}
              style={{ padding: "2px 10px" }}
            >
              {row?.response_status || "Pending"}
            </Tag>
          </div>
        );
      },
    },
    {
      label: "Portal Status",
      key: "proceeding_status",
      filterType: "text",
      filterable: true,
      sortable: true,
      filterName: "proceeding_status",
      render: (row) => {
        return (
          <div className="flex items-center h-14 justify-center">
            <Tag color="#e99210ff" style={{ padding: "2px 10px" }}>
              {row?.proceeding_status || "N/A"}
            </Tag>
          </div>
        );
      },
    },
  ];

  const responseToOutstandingColumns = [
    {
      label: "Client Name",
      key: "client_name",
      filterType: "autocomplete",
      filterable: true,
      sortable: true,
      filterName: "client_name",
    },
    {
      label: "Demand Reference No",
      key: "demand_reference_no",
      filterType: "text",
      filterable: true,
      sortable: true,
      filterName: "demand_reference_no",
    },
    {
      label: "Section Code",
      key: "section_code",
      filterType: "text",
      filterable: true,
      sortable: true,
      filterName: "section_code",
    },
    {
      label: "Assessment Year",
      key: "assessment_year",
      filterType: "text",
      filterable: true,
      sortable: true,
      filterName: "assessmentYear",
    },
    {
      label: "Date of Demand Raised",
      key: "date_of_demand_raised",
      filterType: "date",
      filterable: true,
      sortable: true,
      filterName: "date_of_demand_raised",
      render: (row) => {
        const date = row?.date_of_demand_raised;
        const formattedDate = date
          ? date.split("-").reverse().join("-")
          : "N/A";
        return (
          <div className="flex justify-center mr-2">
            <Tag color="#108ee9">{formattedDate}</Tag>
          </div>
        );
      },
    },
    // {
    //   label: "Date of Service Notice",
    //   key: "date_of_service_notice",
    //   filterType: "date",
    //   filterable: true,
    //   sortable: true,
    //   filterName: "date_of_service_notice",
    //   render: (row) => {
    //     const date = row?.date_of_service_notice;
    //     const formattedDate = date
    //       ? date.split("-").reverse().join("-")
    //       : "N/A";
    //     return (
    //       <div className="flex justify-center mr-2">
    //         <Tag color="#108ee9">{formattedDate}</Tag>
    //       </div>
    //     );
    //   },
    // },
    {
      label: "View Summary",
      key: "view_summary",
      filterable: false,
      filterType: "none",
      preventRowClick: true,
      render: (row) => {
        if (!row?.notice_letter) return null;
        return (
          <div className="ml-14">
            <NoticeSummaryViewer
              variant="table"
              doctype="Response to Outstanding Demand"
              buttonClassName="rounded-full w-4 text-lg"
              docname={row?.name}
            />
          </div>
        );
      },
    },
    {
      label: "Response Status",
      key: "response_status",
      filterType: "select",
      filterable: true,
      sortable: true,
      filterName: "response_status",
      filterOptions: [
        { label: "Pending", value: "Pending" },
        { label: "Response Generated", value: "Response Generated" },
        { label: "Response Finalized", value: "Response Finalized" },
        { label: "Submitted", value: "Submitted" },
      ],
      render: (row) => {
        const statusColors = {
          Pending: "#d9d9d9",
          "Response Generated": "#faad14",
          "Response Finalized": "#1890ff",
          Submitted: "#52c41a",
        };
        return (
          <div className="flex items-center h-14 justify-center">
            <Tag
              color={statusColors[row?.response_status] || "#d9d9d9"}
              style={{ padding: "2px 10px" }}
            >
              {row?.response_status || "Pending"}
            </Tag>
          </div>
        );
      },
    },
    {
      label: "Portal Status",
      key: "notice_status",
      filterType: "text",
      filterable: true,
      sortable: true,
      filterName: "notice_status",
      render: (row) => {
        return (
          <div className="flex items-center h-14 justify-center">
            <Tag color="#e99210ff" style={{ padding: "2px 10px" }}>
              {row?.notice_status || "N/A"}
            </Tag>
          </div>
        );
      },
    },
    // {
    //   label: "Response Type",
    //   key: "response_type",
    //   filterType: "text",
    //   filterable: true,
    //   sortable: true,
    //   filterName: "response_type",
    // },
  ];

  const failedLoginColumns = [
    {
      label: "Client",
      key: "client_name",
      filterable: false,
      filterName: "client_name",
    },
    {
      label: "Portal",
      key: "portal",
      filterable: false,
      filterName: "portal",
    },
    {
      label: "Initiated",
      key: "creation",
      filterable: false,
      render: (row) => {
        const formattedDate = row["creation"]
          ? moment(row["creation"]).format("MMMM Do YYYY, h:mm:ss a")
          : "-";

        return row["creation"] ? (
          <div className="flex items-center h-14 justify-center">
            <Tag
              icon={<CalendarOutlined />}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "200px",
                padding: "5px 10px",
              }}
            >
              {formattedDate}
            </Tag>
          </div>
        ) : (
          <p>-</p>
        );
      },
    },
  ];

  const getNoticePageName = (type) => {
    switch (type) {
      case "open_notices":
        return "Open Notices";
      case "last_3_days_notices":
        return "Notices Received in last 3 days";
      case "due_7_days_notices":
        return "Due within 7 days";
      case "failed_login":
        return "Failed Logins";
      case "last_15_days_notices":
        return "Last 15 Days Notices";
      case "over_due_notices":
        return "Over Due Notices";
      default:
        return "Total Notices";
    }
  };

  // Core delete for a given id array. Extracted so BOTH the "Bulk Delete"
  // (selected ids) button AND the shared "Delete All Notices" button (every id
  // in the current list scope) route through the exact same endpoint/doctype
  // logic. Throws on failure; does NOT toast or refresh.
  const deleteNoticesByIds = async (ids) => {
    if (!ids || ids.length === 0) return;
    const doctype =
      activeNoticeTab === "eproceeding"
        ? "E Proceeding"
        : "Response to Outstanding Demand";
    // Legacy `/api/v1/documents/fast-delete` returned 404 — use the
    // portal-scoped `/api/v1/income-tax/delete-documents` endpoint which
    // the FastAPI backend actually registers.
    await deleteNotices(DOCUMENTS.DELETE_NOTICES_INCOME_TAX, {
      doctype: doctype,
      docnames: JSON.stringify(ids),
    });
  };

  // Post-delete refresh, shared by both delete paths.
  const refreshAfterDelete = () => {
    if (activeNoticeTab === "eproceeding") {
      fetchEProceedingsList(limitStart, limit);
    } else {
      fetchResponseToOutstandingDemand(limitStart, limit);
    }
    setSelectedNoticeIds([]);
  };

  // All ids in the CURRENT list scope (client + filters + active tab), sourced
  // from the same paginator export uses. Feeds "Delete All Notices".
  const fetchAllNoticeIdsForDelete = async () => {
    const rows = await fetchAllForExport();
    return (rows || []).map((r) => r.id || r.name).filter(Boolean);
  };

  const handleDeleteNotices = async () => {
    try {
      await deleteNoticesByIds(selectedNoticeIds);
      toast.info("Deleted Successfully");
    } catch (err) {
      toast.error(err);
    } finally {
      refreshAfterDelete();
    }
  };

  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);

  const handleFetchAllClientsNotices = async (forceSyncClientIds = []) => {
    setIsProcessingAll(true);

    try {
      // Same pattern as Clients.jsx:1300 — backend (income_tax.py:2611)
      // auto-picks ALL active IT clients for current_user.organization_id
      // when client_names is omitted. Works for any org size (50, 334, 1000+).
      // Earlier version pre-fetched a paginated client list capped at 100,
      // which silently dropped clients beyond the first page.
      const payload = { portal_type: "income_tax" };
      if (Array.isArray(forceSyncClientIds) && forceSyncClientIds.length > 0) {
        payload.force_sync_client_ids = forceSyncClientIds;
        payload.client_names = forceSyncClientIds;
      }
      const response = await postData(LAMBDA.TRIGGER_PARALLEL_FETCH, payload);

      const result =
        response?.data?.result || response?.result || response?.message || {};
      setProcessedClients(
        result?.client_names || response?.data?.client_names || []
      );

      // Capture log_name for progress polling
      if (result?.log_name) {
        setParallelFetchLogName(result.log_name);
      }

      setTimeout(() => {
        setShowSuccessModal(true);
      }, 2000);
    } catch (error) {
      // An expired plan is a handled state: the interceptor has already

      // shown the upgrade dialog, so neither the red console line nor the

      // generic toast belongs here.

      if (!isSubscriptionExpiredError(error)) {

        console.error("Error processing all clients:", error);

        toast.error("Failed to queue all clients for processing");

      }
      setIsProcessingAll(false);
    }
  };

  const handleFetchAllFailedLoginClientsNotices = async () => {
    setIsProcessingAll(true);

    try {
      const apiUrl = LAMBDA.FETCH_FAILED_LOGIN;
      const payload = {
        portal: "Income Tax",
      };

      const response = await postData(apiUrl, payload);
      // FastAPI nests payload under data.data.result; fall through to legacy paths.
      const resultData =
        response?.data?.result || response?.result || response?.message || {};

      setProcessedClients(resultData?.client_names || []);

      // Capture log_name only — polling starts when user clicks "Got It" on the
      // Successfully Queued modal (onOk / onCancel / footer Got It all trigger it).
      const logName = resultData?.log_name || resultData?.result?.log_name;
      if (logName) {
        setParallelFetchLogName(logName);
      }

      setTimeout(() => {
        setShowSuccessModal(true);
      }, 2000);
    } catch (error) {
      // An expired plan is a handled state: the interceptor already
      // showed the upgrade dialog — no console noise, no toast.
      if (isSubscriptionExpiredError(error)) {
        setIsProcessingAll(false);
        return;
      }
      console.error("Error processing all clients:", error);
      // Surface the BE's actual error message instead of the generic
      // toast — BE returns clearer reasons like "No failed-login clients
      // to retry" (404). postData throws the parsed BE message as a
      // string in most cases. Same pattern as GST handler.
      const msg =
        typeof error === "string"
          ? error
          : error?.detail?.message ||
            error?.message ||
            error?.detail ||
            "Failed to queue all clients for processing";
      toast.error(msg);
      setIsProcessingAll(false);
    }
  };

  // Start polling the Parallel Fetch Log for progress
  const startBatchPolling = useCallback((logName) => {
    if (batchPollingInterval) {
      clearInterval(batchPollingInterval);
    }

    isBatchFetchActive.current = true;

    setFetchProgress({
      total: 0,
      successful: 0,
      failed: 0,
      status: "Processing",
      isComplete: false,
    });

    const intervalId = setInterval(async () => {
      try {
        const response = await postData(
          LAMBDA.GET_FETCH_STATUS,
          { log_name: logName }
        );
        // FastAPI returns service payload under response.data.result.
        const result =
          response?.data?.result || response?.result || response?.message || {};

        if (result?.status === "success") {
          const progress = {
            total: result.total_clients || 0,
            successful: result.successful_count || 0,
            failed: result.failed_count || 0,
            status: result.fetch_status || "Processing",
            isComplete: result.is_complete || false,
            portalType: result.portal_type || "",
            portalFailureCount: result.portal_failure_count || 0,
            credentialFailureCount: result.credential_failure_count || 0,
          };
          setFetchProgress(progress);

          if (progress.isComplete) {
            clearInterval(intervalId);
            setBatchPollingInterval(null);
            isBatchFetchActive.current = false;
            setSyncCompletionModal(true);
          }
        }
      } catch (err) {
        console.error("Error polling batch status:", err);
      }
    }, 10000);

    setBatchPollingInterval(intervalId);
  }, [batchPollingInterval]);

  // Cleanup batch polling on unmount
  useEffect(() => {
    return () => {
      if (batchPollingInterval) {
        clearInterval(batchPollingInterval);
      }
    };
  }, [batchPollingInterval]);

  // useEffect(() => {
  //   getEproccedingsCount();
  // }, [filterValues]);

  return (
    <>
      <div className="flex w-full justify-between items-center  ">
        {clientId ? (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin/clients"
                          : serviceType == "tds"
                          ? "/tds/clients"
                          : "/income-tax/clients",
                    }}
                  >
                    Clients{" "}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <Link className="exempt-from-readonly" to={-1}>
                    {clientName || "Client Details"}
                  </Link>
                ),
              },
              { title: `Notices` },
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        ) : (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                    onClick={clearSavedFilters}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                    onClick={clearSavedFilters}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <span
                    className="cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      clearSavedFilters();
                      setFilterValues(getDefaultFilters());
                    }}
                  >
                    {getNoticePageName(noticeType)}
                  </span>
                ),
              },
              // Show client name in breadcrumb when filtered
              ...(getFilteredClientName()
                ? [
                    {
                      title: (
                        <span className="text-blue-600 font-medium">
                          {getFilteredClientName()}
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        )}
        <DashboardButton serviceType={serviceType} />
      </div>

      <PageTitle
        title={clientId ? "E-Proceedings" : getNoticePageName(noticeType)}
        // Inside a group (Layer 2) the header arrow returns to the group cards
        // instead of leaving the page; elsewhere it keeps the default history back.
        onBack={showGroupNotices ? backToGroups : undefined}
      />

      {noticeType !== "failed_login" && (
        <div className="flex gap-2 mb-4 mt-4">
          <button
            onClick={() => {
              setActiveNoticeTab("eproceeding");
              setLimitStart(0);
              setFilterValues({});
              setSelectedNoticeIds([]);
              setEpGroupView("groups");
              setSelectedGroup(null);
              setEpFyiTab("fya");
              setEpPanTab("self");
            }}
            className={`flex-1 px-6 py-4 font-semibold text-base transition-all duration-300 relative ${
              activeNoticeTab === "eproceeding"
                ? "bg-gray-100 text-blue-600 border-b-4 border-blue-600"
                : "bg-white text-gray-600 hover:bg-gray-50 border-b-2 border-gray-200"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span> E-Proceeding ({eproceedingscount || 0})</span>
            </div>
          </button>

          <button
            onClick={() => {
              setActiveNoticeTab("response");
              setLimitStart(0);
              setFilterValues({});
              setSelectedNoticeIds([]);
              setEpGroupView("groups");
              setSelectedGroup(null);
              setEpFyiTab("fya");
              setEpPanTab("self");
            }}
            className={`flex-1 px-6 py-4 font-semibold text-base transition-all duration-300 relative ${
              activeNoticeTab === "response"
                ? "bg-gray-100 text-blue-600 border-b-4 border-blue-600"
                : "bg-white text-gray-600 hover:bg-gray-50 border-b-2 border-gray-200"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span>
                {" "}
                Response to Outstanding Demand (
                {responseToOutstandingCount || 0})
              </span>
            </div>
          </button>
        </div>
      )}

      {/* Toolbar layout normalised to match the "perfect" Total Notices page:
          ALL action buttons live in a LEFT group, and Download Data + the User
          filter live in a RIGHT group, split by justify-between. On the Total
          Notices view (no clientId) the left group is empty and Download +
          Users stay on the right exactly as before — only the per-client view
          changes (it used to crowd every button on the right). */}
      <div className="flex flex-wrap items-center w-full justify-between gap-2 pr-4 mt-4">
        {/* LEFT — action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {clientId &&
            searchClientType == "active" &&
            (selectedNoticeIds.length == 0 ? (
              <Tooltip title="Please select a notice">
                <Button
                  type="primary"
                  icon={<DeleteFilled />}
                  disabled
                >
                  Bulk Delete
                </Button>
              </Tooltip>
            ) : (
              <Button
                type={"primary"}
                icon={<DeleteFilled />}
                onClick={() => {
                  setIsDeleteModalOpen(true);
                }}
                disabled={selectedNoticeIds.length > 0 ? false : true}
              >
                {/* One button covers both IT notice types — handleDeleteNotices
                    picks the doctype (E Proceeding / Response to Outstanding
                    Demand) from the active tab. */}
                <span>Bulk Delete</span>
              </Button>
            ))}
          {clientId && searchClientType == "active" && (
            <>
              <BulkStatusButton
                selectedIds={selectedNoticeIds}
                doctype={() => (activeNoticeTab === "eproceeding" ? "E Proceeding" : "Response to Outstanding Demand")}
                onDone={refreshAfterDelete}
              />
              <DeleteAllNoticesButton
                fetchAllIds={fetchAllNoticeIdsForDelete}
                deleteByIds={deleteNoticesByIds}
                onDone={refreshAfterDelete}
                scopeHint="for this client"
                className=""
              />
            </>
          )}
          {clientId && searchClientType == "active" && (
            <Button
              onClick={() => {
                navigate(`/${serviceType}/add-eproceedings/${clientId}`);
              }}
              variant="outlined"
              icon={<FaPlus />}
              type="primary"
            >
              Add New E Proceeding
            </Button>
          )}
          {clientId && searchClientType == "active" && (
            <Button
              type="primary"
              icon={<FaArrowsRotate />}
              onClick={() => {
                if (activeNoticeTab === "eproceeding") {
                  fetchEProceedingsList(limitStart, limit);
                } else {
                  fetchResponseToOutstandingDemand(limitStart, limit);
                }
              }}
            >
              Refresh
            </Button>
          )}
        </div>

        {/* MIDDLE — scope hint for the "Due in 7 days" view. Explains the
            list intentionally includes notices with NULL / missing due dates
            and that closing a notice removes it from this view. Centred in the
            gap between the left action group and the right controls. */}
        {noticeType === "due_7_days_notices" && (
          <div
            className="flex-1 flex justify-center"
            style={{ minWidth: 0 }}
          >
            <span
              style={{
                color: "#6b7280",
                fontSize: 13,
                lineHeight: 1.5,
                fontFamily: "inherit",
                maxWidth: 700,
                textAlign: "center",
              }}
            >
              This section displays notices due within 7 days or those
              with no due date. Once the{" "}
              <b style={{ color: "#374151" }}>Portal Status</b> is
              marked <b style={{ color: "#374151" }}>Closed</b>, the
              notice will be removed automatically.
            </span>
          </div>
        )}

        {/* RIGHT — Download Data + User filter */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Download wired to the CURRENTLY VISIBLE list: the e-proceedings
              tab exports e-proceedings, the demands tab exports outstanding
              demands. Failed-login view has no dedicated notice column-set,
              so it falls back to the e-proceedings entity. */}
          {noticeType !== "failed_login" && (
            <DownloadDataButton
              entity={
                activeNoticeTab === "response"
                  ? "notices-it-demand"
                  : "notices-it-ep"
              }
              noun="notices"
              selectedIds={selectedNoticeIds}
              selectedRows={(activeNoticeTab === "response"
                ? responseOutstandingList
                : ePdataList || []
              ).filter((r) => selectedNoticeIds.includes(r.id || r.name))}
              fetchAllRecords={fetchAllForExport}
              fileBaseName={
                activeNoticeTab === "response"
                  ? "noticeai-it-demands"
                  : "noticeai-it-eproceedings"
              }
              title={
                activeNoticeTab === "response"
                  ? "Outstanding Demands"
                  : "Income Tax E-Proceedings"
              }
            />
          )}
          {/* User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). Dropdown
              self-suppresses for single-user orgs and for regular users. */}
          <UserFilterDropdown
            value={userFilter}
            onChange={setUserFilter}
            portal={serviceType}
          />
        </div>
      </div>

      {noticeType === "total_notices" && (
        <div className="mt-2 mb-2">
          <div className="flex items-center ">
            <div className="flex items-center">
              {!clientId && (
                <>
                  {selectedNoticeIds.length == 0 ? (
                    <Tooltip title="Please select a notice">
                      <div>
                        <Button
                          type="primary"
                          icon={<DeleteFilled />}
                          className="mr-2"
                          disabled
                        >
                          Bulk Delete
                        </Button>
                      </div>
                    </Tooltip>
                  ) : (
                    <Button
                      type={"primary"}
                      icon={<DeleteFilled />}
                      onClick={() => setIsDeleteModalOpen(true)}
                      className="mr-2"
                      disabled={selectedNoticeIds.length > 0 ? false : true}
                    >
                      Bulk Delete
                    </Button>
                  )}
                  <BulkStatusButton
                    selectedIds={selectedNoticeIds}
                    doctype={() => (activeNoticeTab === "eproceeding" ? "E Proceeding" : "Response to Outstanding Demand")}
                    onDone={refreshAfterDelete}
                  />
                  <DeleteAllNoticesButton
                    fetchAllIds={fetchAllNoticeIdsForDelete}
                    deleteByIds={deleteNoticesByIds}
                    onDone={refreshAfterDelete}
                    scopeHint="across all clients"
                  />
                </>
              )}
            </div>
            <Button
              loading={isProcessingAll}
              icon={<Download className="w-4 h-4 mt-2" />}
              type="primary"
              onClick={() => setFetchConfirmOpen(true)}
              style={{
                background: "#1890ff",
                borderColor: "#1890ff",
                border: "1px solid #fff",
                boxShadow: "0 0 16px rgba(30, 64, 175, 0.4",
                borderRadius: "8px",
                padding: "8px 16px",
                fontWeight: "600",
                color: "#fff",
                transition: "box-shadow 0.3s ease",
              }}
              disabled={isProcessingAll}
            >
              {isProcessingAll
                ? "Processing..."
                : clientId
                  ? "Fetch Notices"
                  : "Fetch Notices for All Clients"}
            </Button>
          </div>
        </div>
      )}

      {noticeType == "failed_login" && (
        <div className="my-2">
          <Button
            loading={isProcessingAll}
            icon={<Download className="w-4 h-4 mt-2" />}
            type="primary"
            onClick={handleFetchAllFailedLoginClientsNotices}
            disabled={isProcessingAll || !failedLoginCount}
            title={
              !failedLoginCount
                ? "No failed-login clients to retry"
                : undefined
            }
          >
            {isProcessingAll
              ? "Processing..."
              : `Fetch notices for all Failed Login Clients`}
          </Button>
        </div>
      )}

      <div
        className="border-[2px] mt-4 rounded-2xl"
        style={{
          borderColor: "#d1d5db",
        }}
      >
        {/* Batch fetch progress banner */}
        {fetchProgress && !fetchProgress.isComplete && (
          <div
            style={{
              marginBottom: "16px",
              padding: "16px 20px",
              background: "linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)",
              borderRadius: "12px",
              border: "1px solid #91d5ff",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <SyncOutlined spin style={{ color: "#1890ff", fontSize: "16px" }} />
                <Text strong style={{ fontSize: "14px" }}>
                  Fetching Income Tax Notices...
                </Text>
              </div>
              <Text style={{ color: "#666", fontSize: "13px" }}>
                {fetchProgress.successful + fetchProgress.failed} / {fetchProgress.total} clients processed
              </Text>
            </div>
            <Progress
              percent={fetchProgress.total > 0 ? Math.round(((fetchProgress.successful + fetchProgress.failed) / fetchProgress.total) * 100) : 0}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              size="small"
            />
            <div style={{ display: "flex", gap: "16px", marginTop: "6px", fontSize: "12px", color: "#666" }}>
              <span style={{ color: "#52c41a" }}>{fetchProgress.successful} successful</span>
              {fetchProgress.failed > 0 && <span style={{ color: "#ff4d4f" }}>{fetchProgress.failed} failed</span>}
            </div>
          </div>
        )}

        {showGroupCards ? (
          /* LAYER 1 — grouped cards (proceeding_name + assessment_year),
             portal-style. Click a card to drill into that group's notices. */
          <div className="mt-4">
            {/* Total Notices search + filter bar. Only on the Total Notices
                view (not the per-client active view). Binds straight into
                filterValues; buildParamsForTab already sends these to the
                backend and the grouping useEffect refetches on change. */}
            {noticeType === "total_notices" && (
              <div className="mb-4 p-3 rounded-xl bg-gray-50 border border-gray-200">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">
                      Notice ID / DIN
                    </label>
                    <Input
                      allowClear
                      style={{ width: 190 }}
                      placeholder="Search notice ID / DIN"
                      value={filterValues.notice_din || ""}
                      onChange={(e) =>
                        handleFilterValueChange(
                          "notice_din",
                          e.target.value || null
                        )
                      }
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">
                      Reference / Communication ID
                    </label>
                    <Input
                      allowClear
                      style={{ width: 220 }}
                      placeholder="Search reference / comm. ID"
                      value={
                        filterValues.notice_communication_reference_id || ""
                      }
                      onChange={(e) =>
                        handleFilterValueChange(
                          "notice_communication_reference_id",
                          e.target.value || null
                        )
                      }
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">
                      Assessment Year
                    </label>
                    <Input
                      allowClear
                      style={{ width: 150 }}
                      placeholder="e.g. 2024-25"
                      value={filterValues.assessmentYear || ""}
                      onChange={(e) =>
                        handleFilterValueChange(
                          "assessmentYear",
                          e.target.value || null
                        )
                      }
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">
                      Response Status
                    </label>
                    <Select
                      allowClear
                      style={{ width: 190 }}
                      placeholder="All statuses"
                      value={filterValues.response_status || undefined}
                      onChange={(v) =>
                        handleFilterValueChange("response_status", v || null)
                      }
                    >
                      <Option value="Pending">Pending</Option>
                      <Option value="Response Generated">
                        Response Generated
                      </Option>
                      <Option value="Response Finalized">
                        Response Finalized
                      </Option>
                      <Option value="Submitted">Submitted</Option>
                    </Select>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">
                      Issued Date
                    </label>
                    <RangePicker
                      format="YYYY-MM-DD"
                      style={{ width: 250 }}
                      placeholder={["Start Date", "End Date"]}
                      value={
                        Array.isArray(filterValues.noticeSentDate) &&
                        filterValues.noticeSentDate.length === 2
                          ? filterValues.noticeSentDate
                          : null
                      }
                      onChange={(dates) =>
                        handleFilterValueChange(
                          "noticeSentDate",
                          dates && dates.length === 2 ? [dates[0], dates[1]] : null
                        )
                      }
                    />
                  </div>
                  {hasActiveTotalNoticesFilter && (
                    <Button onClick={clearTotalNoticesFilters}>
                      Clear filters
                    </Button>
                  )}
                </div>
              </div>
            )}
            {/* Level 1 — Self / "Of Other PAN/TAN" toggle, mirroring the
                portal's "View e-Proceeding related to: Self | Of Other PAN/TAN".
                Switching resets the FYA/FYI sub-tab + card view. */}
            <div className="mb-3">
              <span className="block text-xs font-medium text-gray-500 mb-1">
                View e-Proceeding related to:
              </span>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                {[
                  { key: "self", label: "Self", n: selfProceedingCount },
                  {
                    key: "other",
                    label: "Of Other PAN/TAN",
                    n: otherProceedingCount,
                  },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => {
                      setEpPanTab(t.key);
                      setEpFyiTab("fya");
                      setEpGroupView("groups");
                      setSelectedGroup(null);
                      setSelectedNoticeIds([]);
                    }}
                    className={`px-5 py-2 text-sm font-semibold transition-all ${
                      epPanTab === t.key
                        ? "bg-blue-800 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {t.label} ({t.n})
                  </button>
                ))}
              </div>
            </div>
            {/* Level 2 — For-Your-Action / For-Your-Information sub-tabs (scoped
                to the selected Self/Other-PAN-TAN type), mirroring the portal.
                Switching resets to the card view. */}
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 mb-4">
              {[
                { key: "fya", label: "For Your Action", n: fyaGroups.length },
                { key: "fyi", label: "For Your Information", n: fyiGroups.length },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    setEpFyiTab(t.key);
                    setEpGroupView("groups");
                    setSelectedGroup(null);
                    setSelectedNoticeIds([]);
                  }}
                  className={`px-5 py-2 text-sm font-semibold rounded-md transition-all ${
                    epFyiTab === t.key
                      ? "bg-blue-600 text-white shadow-sm"
                      : "bg-transparent text-gray-600 hover:text-blue-600"
                  }`}
                >
                  {t.label} ({t.n})
                </button>
              ))}
            </div>
            {groupsLoading ? (
              <div className="flex justify-center items-center py-16">
                <Spin />
              </div>
            ) : epGroups.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                {epFyiTab === "fyi"
                  ? "No 'For Your Information' proceedings found"
                  : "No 'For Your Action' proceedings found"}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {epGroups.map((g) => (
                  <div
                    key={g.groupKey}
                    className="flex flex-col border border-gray-200 rounded-2xl bg-white p-5 shadow-sm hover:shadow-md transition"
                  >
                    <h3 className="text-base font-semibold text-gray-800 leading-snug">
                      {g.proceeding_name}
                    </h3>
                    <div className="mt-3 text-sm text-gray-600">
                      <span className="font-medium text-gray-700">
                        Assessment Year:
                      </span>{" "}
                      {g.assessment_year}
                    </div>
                    {/* For "Of Other PAN/TAN" proceedings, show WHOSE proceeding
                        it is (name + PAN), exactly as the portal does — otherwise
                        a third-party card is indistinguishable from a Self one. */}
                    {g.proceeding_type === "Of Other PAN/TAN" &&
                      (g.name_of_assessee || g.pan) && (
                        <div className="mt-2 text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-md px-2 py-1">
                          {g.name_of_assessee && (
                            <div>
                              <span className="font-medium text-gray-700">
                                Assessee:
                              </span>{" "}
                              {g.name_of_assessee}
                            </div>
                          )}
                          {g.pan && (
                            <div>
                              <span className="font-medium text-gray-700">
                                PAN:
                              </span>{" "}
                              {g.pan}
                            </div>
                          )}
                        </div>
                      )}
                    {g.latestIssueDate > 0 && (
                      <div className="mt-1 text-xs text-gray-400">
                        Latest issued:{" "}
                        {moment(g.latestIssueDate).format("DD MMM YYYY")}
                      </div>
                    )}
                    {/* Two actions, same split as the portal: the closure ORDER
                        gets its own (secondary/outlined) button and is only
                        clickable when the group actually has one; the notices
                        open via the primary button, whose count excludes the
                        closure order. Both use the app's existing AntD theme. */}
                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                      <Button
                        disabled={g.orderCount === 0}
                        onClick={() => openGroup(g, "orders")}
                      >
                        View Closure Order
                      </Button>
                      <Button
                        type="primary"
                        disabled={g.count === 0}
                        onClick={() => openGroup(g, "notices")}
                      >
                        {g.count === 1
                          ? "View Notice/Order (1)"
                          : `View Notices/Orders (${g.count})`}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <TableV2
              /* LAYER 2 heading — which group is open, on ONE line, centered in
                 the table header's empty space. The page header's back arrow
                 (PageTitle onBack) is what returns to the group cards. */
              headerCenter={
                showGroupNotices ? (
                  <div className="bg-white rounded-xl shadow-md border border-gray-200 px-6 py-2 whitespace-nowrap">
                    <span className="text-base font-bold text-blue-900">
                      {selectedGroup.proceeding_name}
                    </span>
                    <span className="mx-2 text-gray-300">|</span>
                    <span className="text-sm text-gray-600">
                      Assessment Year:{" "}
                      <span className="font-semibold text-gray-800">
                        {selectedGroup.assessment_year}
                      </span>
                    </span>
                    <span className="mx-2 text-gray-300">|</span>
                    <span className="text-sm font-semibold text-gray-800">
                      {selectedGroup.viewMode === "orders"
                        ? groupRows.length === 1
                          ? "Closure Order"
                          : `Closure Orders (${groupRows.length})`
                        : `${groupRows.length} ${
                            groupRows.length === 1 ? "notice" : "notices"
                          }`}
                    </span>
                  </div>
                ) : null
              }
              columns={(
                noticeType == "failed_login"
                  ? failedLoginColumns
                  : activeNoticeTab === "eproceeding"
                  ? eProceedingColumns
                  : responseToOutstandingColumns
              ).filter(
                // Hide the whole "View Summary" column (header + cells) when the
                // plan doesn't include VIEW_NOTICE_SUMMARY. The per-row button is
                // already gated by NoticeSummaryViewer, but without this the
                // column header + empty cells still render.
                (col) => col.key !== "view_summary" || summaryFeatureEnabled
              )}
              data={
                showGroupNotices
                  ? groupRows
                  : noticeType == "failed_login"
                  ? failedLoginList
                  : activeNoticeTab == "eproceeding"
                  ? ePdataList
                  : responseOutstandingList
              }
              itemsPerPage={10}
              isLoading={showGroupNotices ? false : loading}
              limitStart={limitStart}
              // Group notices are already fully loaded client-side — show them
              // all (a group is small) instead of server-paginating.
              limit={showGroupNotices ? 1000 : limit}
              setLimitStart={setLimitStart}
              totalRecords={
                showGroupNotices
                  ? groupRows.length
                  : noticeType == "failed_login"
                  ? failedLoginCount
                  : activeNoticeTab === "eproceeding"
                  ? eproceedingscount
                  : responseToOutstandingCount
              }
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              type={noticeType == "failed_login" ? "failed_login" : "eproceeding"}
              rowRedirection={
                noticeType == "failed_login"
                  ? `/${ServiceType.INCOME_TAX}/failed-login-details`
                  : `/${ServiceType.INCOME_TAX}/eproceeding-details`
              }
              selectedIds={selectedNoticeIds}
              setSelectedIds={setSelectedNoticeIds}
              filterValues={filterValues}
              handleFilterValueChange={handleFilterValueChange}
              statusOptions={statusOptions}
              serviceType={serviceType}
              title={getNoticePageName(noticeType)}
              onFetchAllRecords={fetchAllForExport}
            />
          </>
        )}
      </div>

      <Modal
        title=""
        open={isDeleteModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
        zIndex={9999}
        style={{ top: -50 }}
      >
        <div className="flex items-center justify-center flex-col">
          <img src={WarningImage} width={250} height={250} />
          <p>Are you sure you want to Delete ?</p>
        </div>
      </Modal>

      {/* Initial queued modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckCircle color="#52c41a" size={20} />
            <span>Successfully Queued</span>
          </div>
        }
        open={showSuccessModal}
        onOk={() => {
          setShowSuccessModal(false);
          if (parallelFetchLogName) {
            startBatchPolling(parallelFetchLogName);
          }
        }}
        onCancel={() => {
          setShowSuccessModal(false);
          if (parallelFetchLogName) {
            startBatchPolling(parallelFetchLogName);
          }
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setShowSuccessModal(false);
              if (parallelFetchLogName) {
                startBatchPolling(parallelFetchLogName);
              }
            }}
          >
            Got It
          </Button>,
        ]}
        width={500}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "16px", fontSize: "16px" }}>
            <strong>
              Notice fetching has been successfully queued for failed login clients.
            </strong>
          </p>

          <p style={{ marginBottom: "16px", color: "#666" }}>
            Notices are being fetched in the background. You will see a live
            progress bar and a notification when the process completes.
          </p>

          {processedClients.length > 0 && (
            <div>
              <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Queued for {processedClients.length} clients:
              </p>
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  background: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {processedClients.map((client, index) => (
                  <div key={index} style={{ padding: "2px 0" }}>
                    • {client}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Sync completion modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <CheckCircleOutlined
              style={{ color: fetchProgress?.failed > 0 ? "#faad14" : "#52c41a", fontSize: "20px" }}
            />
            <span>
              {fetchProgress?.failed > 0 && fetchProgress?.successful === 0
                ? "Sync Process Failed"
                : "Sync Process Completed"}
            </span>
          </div>
        }
        open={syncCompletionModal}
        onCancel={() => {
          setSyncCompletionModal(false);
          setFetchProgress(null);
          setParallelFetchLogName(null);
          setIsProcessingAll(false);
          fetchFailedLoginList(limitStart, limit);
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setSyncCompletionModal(false);
              setFetchProgress(null);
              setParallelFetchLogName(null);
              setIsProcessingAll(false);
              fetchFailedLoginList(limitStart, limit);
            }}
          >
            OK
          </Button>,
        ]}
        width={500}
        centered
        closable={false}
      >
        <div style={{ padding: "16px 0" }}>
          {fetchProgress ? (
            <>
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Title level={4} style={{ color: fetchProgress.failed > 0 ? "#faad14" : "#52c41a", marginBottom: "8px" }}>
                  Income Tax Notice Fetching Complete
                </Title>
                <Text type="secondary">
                  All {fetchProgress.total} clients have been processed.
                </Text>
              </div>

              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "32px",
                marginBottom: "16px",
                padding: "16px",
                background: "#fafafa",
                borderRadius: "8px",
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#52c41a" }}>
                    {fetchProgress.successful}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Successful</Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: fetchProgress.failed > 0 ? "#ff4d4f" : "#d9d9d9" }}>
                    {fetchProgress.failed}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Failed</Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1890ff" }}>
                    {fetchProgress.total}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Total</Text>
                </div>
              </div>

              {fetchProgress.failed > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  {fetchProgress.portalFailureCount > 0 && (
                    <Alert
                      message={`${fetchProgress.portalFailureCount} client(s) could not be fetched due to portal connectivity issues. You may retry later.`}
                      type="warning"
                      showIcon
                      style={{ marginBottom: "8px" }}
                    />
                  )}
                  {fetchProgress.credentialFailureCount > 0 && (
                    <Alert
                      message={`${fetchProgress.credentialFailureCount} client(s) failed due to login credentials / other issues.`}
                      type="error"
                      showIcon
                      style={{ marginBottom: "8px" }}
                    />
                  )}
                  <Text type="secondary" style={{ fontSize: "13px", display: "block", textAlign: "center", marginTop: "4px" }}>
                    Check the Failed Login tab for details.
                  </Text>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              <Title level={4} style={{ color: "#52c41a", marginBottom: "8px" }}>
                Notice Syncing Completed!
              </Title>
            </div>
          )}
        </div>
      </Modal>

      <FetchNoticesConfirmModal
        open={fetchConfirmOpen}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={(ids) => {
          setFetchConfirmOpen(false);
          handleFetchAllClientsNotices(ids);
        }}
        portal={serviceType || "income-tax"}
        // Scope the popup (and therefore the fetch) to THIS client when the
        // page is opened for one client — e.g. arriving from the Total Notices
        // picker at `eproceedings/:clientId?type=total_notices`. Without it the
        // total_notices toolbar offered an all-clients fetch even though the
        // list itself is already filtered to the single client. `undefined` on
        // the all-clients view keeps the original every-client behaviour.
        clientIds={clientId ? [clientId] : undefined}
      />
    </>
  );
};
